import React from 'react'
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Divider,
  Typography,
  Space,
  Card,
  Row,
  Col
} from 'antd'
import {
  ShoppingOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  TagOutlined,
  WarningOutlined,
  TeamOutlined,
  StarOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { Order, OrderItem } from '../../Types/order.interface'
import './OrderDetailsModal.scss'

const { Title, Text } = Typography

interface OrderDetailsModalProps {
  visible: boolean
  order: Order
  onClose: () => void
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  visible,
  order,
  onClose
}) => {
  const formatDateTime = (dateTime: number | string) => {
    const date = new Date(dateTime)
    return date.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  }

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      PAYMENTCOMPLETED: 'blue',
      ONLAZYTRACK: 'cyan',
      PREPARING: 'orange',
      READY: 'green',
      SERVING: 'purple',
      COMPLETED: 'success'
    }
    return statusColors[status.toUpperCase()] || 'default'
  }

  const orderItemColumns: ColumnsType<OrderItem> = [
    {
      title: 'Item',
      dataIndex: 'label',
      key: 'label',
      render: (text: string, record: OrderItem) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: '14px' }}>
            {text || `Food ID: ${record.foodId}`}
            {record.foodStatus && record.foodStatus !== 'Available' && (
              <Tag color='red' style={{ marginLeft: '8px', fontSize: '11px' }}>
                {record.foodStatus}
              </Tag>
            )}
          </div>
          {record.subCategory && (
            <Text type='secondary' style={{ fontSize: '12px', display: 'block' }}>
              {record.subCategory}
            </Text>
          )}
          {record.comments && (
            <div style={{ marginTop: '4px' }}>
              <Text type='warning' style={{ fontSize: '12px', fontStyle: 'italic' }}>
                Note: {record.comments}
              </Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => type && <Tag color='blue'>{type}</Tag>
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
      render: (qty: number) => <Text strong>×{qty}</Text>
    },
    {
      title: 'Price',
      dataIndex: 'originalBasePrice',
      key: 'originalBasePrice',
      width: 100,
      align: 'right',
      render: (val: string, record: OrderItem) => {
        const base = Number(record.originalBasePrice || record.price || 0)
        const discount = Number(record.discountAmount || 0)
        return (
          <div>
            <div>₹{base.toFixed(2)}</div>
            {discount > 0 && (
              <Text type="danger" style={{ fontSize: '11px' }}>
                -₹{discount.toFixed(2)}
              </Text>
            )}
          </div>
        )
      }
    },
    {
      title: 'GST',
      dataIndex: 'gstAmount',
      key: 'gstAmount',
      width: 80,
      align: 'right',
      render: (gst: string) => `₹${parseFloat(gst).toFixed(2)}`
    },
    {
      title: 'Packaging',
      dataIndex: 'packagingCharges',
      key: 'packagingCharges',
      width: 100,
      align: 'right',
      render: (charge: string) => `₹${parseFloat(charge).toFixed(2)}`
    }
  ]

  const calculateSubtotal = () => {
    return Number(order.orderTotalWithoutDiscount || 0) || order.orderItems.reduce((total, item) => {
      return total + Number(item.originalBasePrice || item.price || 0)
    }, 0)
  }

  const calculateTotalGST = () => {
    const foodGst = order.orderItems.reduce((total, item) => {
      return total + Number(item.gstAmount || 0)
    }, 0)
    const pkgGst = Number(order.packagingGstTotal || 0)
    return foodGst + pkgGst
  }

  const calculateTotalPackaging = () => {
    return order.orderItems.reduce((total, item) => {
      return total + Number(item.packagingCharges || 0)
    }, 0)
  }

  const calculateTotalDiscount = () => {
    return Number(order.discountAmount || 0)
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ShoppingOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
          <span style={{ fontSize: '18px', fontWeight: 600 }}>
            Order Details
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      className='order-details-modal'
    >
      <div style={{ padding: '8px 0' }}>
        {/* Order Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={8}>
            <Card size='small' className='info-card'>
              <Space direction='vertical' size={4}>
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  <TagOutlined /> Order ID
                </Text>
                <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                  {order.orderId}
                </Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size='small' className='info-card'>
              <Space direction='vertical' size={4}>
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  Status
                </Text>
                <Tag
                  color={getStatusColor(order.orderStatus)}
                  style={{ margin: 0, padding: '4px 12px' }}
                >
                  {order.orderStatus}
                </Tag>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size='small' className='info-card'>
              <Space direction='vertical' size={4}>
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  <DollarOutlined /> Total Amount
                </Text>
                <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                  ₹{Number(order.totalAmount || 0).toFixed(2)}
                </Text>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Order Information */}
        <Card
          title='Order Information'
          size='small'
          style={{ marginBottom: '16px' }}
        >
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size='small' layout='vertical'>
            <Descriptions.Item
              label={
                <>
                  <UserOutlined /> Customer ID
                </>
              }
            >
              <Text strong>{order.userId}</Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <>
                  <ClockCircleOutlined /> Order Date & Time
                </>
              }
            >
              <Text strong>{formatDateTime(order.orderDateTime)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='Order Type'>
              <Tag color='blue'>{order.orderType}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label='Service Type'>
              <Tag color='cyan'>{order.serviceType}</Tag>
            </Descriptions.Item>
            {order.headCount && (
              <Descriptions.Item
                label={
                  <>
                    <TeamOutlined /> Head Count
                  </>
                }
              >
                <Text strong>{order.headCount}</Text>
              </Descriptions.Item>
            )}
            {order.requestedAmmenities &&
              order.requestedAmmenities.length > 0 && (
                <Descriptions.Item
                  label={
                    <>
                      <StarOutlined /> Amenities
                    </>
                  }
                  span={3}
                >
                  <Space size={[0, 8]} wrap>
                    {order.requestedAmmenities.map((amenity, index) => (
                      <Tag color='purple' key={index} style={{ margin: 0 }}>
                        {amenity.ammenityName}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
            {order.orderReachTime && (
              <Descriptions.Item
                label={
                  <>
                    <ClockCircleOutlined /> Expected Reach Time
                  </>
                }
              >
                <Text strong>{formatDateTime(order.orderReachTime)}</Text>
              </Descriptions.Item>
            )}
            {order.orderDistance && (
              <Descriptions.Item
                label={
                  <>
                    <EnvironmentOutlined /> Distance
                  </>
                }
              >
                <Text strong>{order.orderDistance}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Order Items */}
        <Card title='Order Items' size='small' style={{ marginBottom: '16px' }}>
          {order.hasPartialFood && (
            <div
              style={{
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: '4px',
                padding: '8px 12px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <WarningOutlined style={{ color: '#faad14', fontSize: '16px' }} />
              <Text style={{ color: '#ad6800' }}>
                Some items in this order are unavailable or out of stock
              </Text>
            </div>
          )}
          <Table
            columns={orderItemColumns}
            dataSource={order.orderItems}
            rowKey={(record) => `${record.foodId}-${record.categoryId}`}
            pagination={false}
            size='small'
            bordered
            scroll={{ x: 600 }}
            rowClassName={(record) =>
              record.foodStatus && record.foodStatus !== 'Available'
                ? 'unavailable-item-row'
                : ''
            }
            summary={() => (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3} align='right'>
                    <Text strong>Subtotal (Food)</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align='right'>
                    <Text strong>₹{calculateSubtotal().toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align='right'>
                    <Text strong>₹{calculateTotalGST().toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align='right'>
                    <Text strong>₹{calculateTotalPackaging().toFixed(2)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                
                {calculateTotalDiscount() > 0 && (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3} align='right'>
                      <Text strong type="danger">Vendor Discount Applied</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} colSpan={3} align='right'>
                      <Text strong type="danger">
                        -₹{calculateTotalDiscount().toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}

                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3} align='right'>
                    <Text strong style={{ fontSize: '16px' }}>
                      Grand Total
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={3} align='right'>
                    <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                      ₹{Number(order.totalAmount || 0).toFixed(2)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            )}
          />
        </Card>

        {/* Order Comments */}
        {order.comments && (
          <Card title='Special Instructions' size='small'>
            <Text style={{ fontSize: '14px' }}>{order.comments}</Text>
          </Card>
        )}
      </div>
    </Modal>
  )
}

export default OrderDetailsModal
